
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ShieldCheck, ArrowRight, MessageSquare, TrendingUp, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SubscriptionModel from '../pricing/SubscriptionModel';

const ScaleIllustration = ({ isCutshort = false }: { isCutshort?: boolean }) => (
  <div className={`p-6 rounded-2xl border flex flex-col items-center justify-between min-h-[280px] w-full transition-all duration-500 ${isCutshort ? 'bg-emerald-50/30 border-emerald-100 shadow-sm' : 'bg-red-50/30 border-red-100 opacity-80'}`}>
    <div className="text-center mb-4">
      <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${isCutshort ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
        {isCutshort ? 'The Cutshort Model' : 'Traditional Model'}
      </span>
    </div>

    <div className="relative w-full h-32 flex items-center justify-center">
      {/* Scale Arm */}
      <motion.div 
        animate={{ 
          rotate: isCutshort ? [0, -2, 2, 0] : -25 
        }}
        transition={{ 
          duration: isCutshort ? 4 : 0.5, 
          repeat: isCutshort ? Infinity : 0,
          ease: "easeInOut" 
        }}
        className="relative w-48 h-1 bg-brand-darkgrey/20 rounded-full"
      >
        {/* Left Side (Costs) */}
        <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2">
          <motion.div 
            animate={isCutshort ? { y: [0, -5, 0] } : { y: 20 }}
            transition={{ duration: 4, repeat: Infinity }}
            className={`flex flex-col items-center gap-2`}
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ${isCutshort ? 'bg-white text-emerald-500 border border-emerald-100' : 'bg-brand-darkgrey text-white'}`}>
              <TrendingUp className="w-6 h-6" />
            </div>
            <span className="text-[10px] font-bold text-brand-darkgrey text-center leading-none whitespace-nowrap">
              {isCutshort ? 'Your\nInvestment' : 'Large Upfront\nCost'}
            </span>
          </motion.div>
        </div>

        {/* Right Side (Results) */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2">
          <motion.div 
            animate={isCutshort ? { y: [0, -5, 0] } : { y: -20 }}
            transition={{ duration: 4, repeat: Infinity }}
            className="flex flex-col items-center gap-2"
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ${isCutshort ? 'bg-brand-blue text-white' : 'bg-white text-red-400 border border-red-100'}`}>
              {isCutshort ? <Check className="w-6 h-6 stroke-[3px]" /> : <Zap className="w-4 h-4 fill-current opacity-30" />}
            </div>
            <span className="text-[10px] font-bold text-brand-darkgrey text-center leading-none whitespace-nowrap">
              {isCutshort ? 'Successful\nCandidates' : 'Potential\nHires'}
            </span>
          </motion.div>
        </div>
      </motion.div>

      {/* Scale Base */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-8 flex flex-col items-center">
        <div className="w-1 h-full bg-brand-darkgrey/20" />
        <div className="w-6 h-1 bg-brand-darkgrey/20 rounded-full" />
      </div>
    </div>

    <div className="mt-6 text-center">
      <p className={`text-xs font-bold leading-tight ${isCutshort ? 'text-emerald-700' : 'text-red-700 opacity-60'}`}>
        {isCutshort ? '$Investment = Results' : 'High Risk, Uncertain ROI'}
      </p>
    </div>
  </div>
);

const PricingSection = () => {
  const [searchParams] = useSearchParams();
  const selectedPlan = searchParams.get('plan') === 'flexi' ? 'flexi' : 'fixed';

  return (
    <section className="section-padding bg-[#FDFDFF] relative overflow-hidden">
      {/* Background patterns */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" 
        style={{ backgroundImage: 'radial-gradient(#5851D3 1px, transparent 1px)', backgroundSize: '32px 32px' }} 
      />
      
      <div className="container-custom relative z-10">
        <AnimatePresence mode="wait">
          {selectedPlan === 'fixed' ? (
            <motion.div
              key="fixed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="text-center max-w-4xl mx-auto mb-16">
                <motion.h1 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-4xl md:text-6xl font-extrabold text-brand-darkgrey leading-tight mb-6"
                >
                  Simple pricing with <span className="text-brand-blue">unparalled ROI</span>
                </motion.h1>
                <motion.h2 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-2xl md:text-3xl text-brand-grey font-bold"
                >
                  Hire in 3 days, not 30. Cutshort pays for itself by reducing your spend on costly resume databases and job postings.
                </motion.h2>
              </div>
              <SubscriptionModel showSmartROI={true} hideComparison={true} />
            </motion.div>
          ) : (
            <motion.div
              key="flexi"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24"
            >
              {/* Left: Copy and Scale Illustration */}
              <div className="flex-1 space-y-12">
                <div className="space-y-6">
                  <motion.p
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    className="text-sm md:text-base text-brand-grey font-semibold uppercase tracking-wide"
                  >
                    For the first time ever
                  </motion.p>
                  <motion.h1 
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.05 }}
                    className="text-4xl md:text-6xl font-extrabold text-brand-darkgrey leading-tight"
                  >
                    Pay for candidates you pursue. <br />
                    <span className="text-brand-blue font-extrabold">Not for databases you have to search.</span>
                  </motion.h1>
                  <motion.h2 
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 }}
                    className="text-2xl md:text-3xl text-brand-grey font-bold max-w-xl"
                  >
                    No huge upfront payments. Assured ROI.
                  </motion.h2>
                </div>

                <div className="space-y-8">
                  <div className="grid grid-cols-2 gap-4">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <ScaleIllustration />
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 }}
                    >
                      <ScaleIllustration isCutshort />
                    </motion.div>
                  </div>
                </div>
              </div>

              {/* Right: The CTA Box */}
              <div className="flex-1 w-full max-w-[500px]">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-[40px] p-8 md:p-12 shadow-2xl border border-brand-blue/10 relative overflow-hidden"
                >
                  {/* Subtle background element */}
                  <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-64 h-64 bg-brand-blue/5 blur-3xl rounded-full" />
                  
                  <div className="relative z-10 space-y-8">
                    <h3 className="text-3xl md:text-4xl font-extrabold text-brand-darkgrey mb-2">
                      Flexi Usage Plan
                    </h3>
                    <div className="space-y-6">
                      {[
                        "No capacity planning needed. Scale as per your needs.",
                        "Seamless - get priority pipeline in your ATS.",
                        "Cutshort platform access for your recruiting team"
                      ].map((text, i) => (
                        <motion.div 
                          key={i}
                          initial={{ opacity: 0, x: 20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.3 + i * 0.1 }}
                          className="flex items-start gap-4"
                        >
                          <div className="mt-1 w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                            <Check className="w-4 h-4 text-emerald-600 stroke-[3px]" />
                          </div>
                          <p className="text-lg font-bold text-brand-darkgrey leading-snug">
                            {text}
                          </p>
                        </motion.div>
                      ))}
                    </div>

                    <div className="pt-6 space-y-6">
                      <Button 
                        asChild
                        className="w-full h-16 rounded-2xl bg-brand-blue hover:bg-brand-blue/90 text-xl font-bold shadow-lg shadow-brand-blue/20 flex items-center justify-center gap-2 group"
                      >
                        <a href="https://share.hsforms.com/1y0l7U0OHRo2JLsKVJLBhnAbpcum" target="_blank" rel="noopener noreferrer">
                          Request a callback
                          <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                        </a>
                      </Button>
                      
                      <div className="flex items-center justify-center gap-3 px-5 py-3 rounded-2xl bg-emerald-50 border border-emerald-100 text-emerald-700">
                        <ShieldCheck className="w-6 h-6" />
                        <span className="font-bold">100% refund if not satisfied</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
                
                {/* Visual element suggesting "breaking free" */}
                <div className="mt-8 flex items-center justify-center gap-4 text-brand-blue/40 font-bold uppercase tracking-widest text-xs">
                  <MessageSquare className="w-4 h-4" />
                  <span>Scale your hiring today</span>
                  <MessageSquare className="w-4 h-4" />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default PricingSection;
